import { Injectable } from '@angular/core'
import {
  AudioSegmentPlaybackData,
  AudioSegmentShareStatus,
  AudioSegmentShareStatusResponse,
} from '@ftr/contracts/api/audio-segment'
import { SealedSegments } from '@ftr/contracts/api/court-recording'
import { HasBeenReleasedToUserResponse } from '@ftr/contracts/api/shared'
import { ProducerCondition } from '@ftr/contracts/regional-api'
import { DataStore, ONE_SECOND_MS, Uuid } from '@ftr/contracts/type/shared'
import {
  ApiResult,
  distinctUntilDataChanged,
  mapData,
  recover,
  recoverType,
  recoverUndefinedData,
  skipFutureNonSuccess,
} from '@ftr/foundation'
import { plainToClass } from '@ftr/serialization'
import { switchMap, timer } from 'rxjs'
import { ApiClient } from '~app/services/ftr-api/api-client'
import { ApiClientFactory } from '~app/services/ftr-api/api-client.factory'
import { NotFoundApiError } from '~app/services/ftr-api/not-found-api.error'
import { RegionalApiClientSetupService } from '~app/services/ftr-regional-api/regional-api-client-setup.service'
import { RegionalApiClientFactory } from '~app/services/ftr-regional-api/regional-api-client.factory'
import { HttpResponseType } from '~app/services/http-client/http-response-type'
import { hasEqualSegments } from '~app/util/recording-live'
import { MultiChannelPlaylistService } from '../multi-channel-playlist/multi-channel-playlist.service'

const API_PATH = 'audio-segment'
export const AUDIO_SEGMENT_POLL_FREQUENCY = 3 // seconds

@Injectable()
export class AudioSegmentService extends RegionalApiClientSetupService {
  private readonly apiClient: ApiClient

  constructor(
    apiClientFactory: ApiClientFactory,
    regionalApiClientFactory: RegionalApiClientFactory,
    private readonly multiChannelPlaylistService: MultiChannelPlaylistService,
  ) {
    super(regionalApiClientFactory)
    this.apiClient = apiClientFactory.build(`/${API_PATH}`)
  }

  getWithRecordingSegments(audioSegmentId: Uuid): ApiResult<AudioSegmentPlaybackData> {
    return this.apiClient.get<AudioSegmentPlaybackData>({
      path: `/${audioSegmentId}/getWithRecordingSegments`,
      responseBodyType: AudioSegmentPlaybackData,
    })
  }

  /**
   * This audio segment is or was live and comes from regional api
   * Used for real-time ordering and shared recording playback
   */
  getWithRecordingSegmentsLive(audioSegmentId: Uuid, courtSystemId: Uuid): ApiResult<AudioSegmentPlaybackData> {
    return this.regionalApiClient(API_PATH, courtSystemId).pipe(
      switchMap(client =>
        client.get<AudioSegmentPlaybackData>({
          path: `/${audioSegmentId}`,
          responseBodyType: AudioSegmentPlaybackData,
        }),
      ),
    )
  }

  playlistUrl(audioSegment: AudioSegmentPlaybackData): string {
    const playlistUrl = this.multiChannelPlaylistService.getMultiChannelUrlWhenValid(
      audioSegment.playlistUrl,
      audioSegment.hasMultiChannel,
    )

    // Return a fully-qualified URL whole
    if (playlistUrl.toLowerCase().startsWith('http')) {
      return playlistUrl
    }
    // Otherwise, append the full path to the API root.
    const apiRoot = this.apiClient.baseUrl.split('/v1/api')[0]

    return `${apiRoot}${playlistUrl}`
  }

  /**
   * TODO OR-1553 To manually poll the playlist until it becomes available.
   * Do not use for any other purposes, because it swallows not found errors.
   */
  getPlaylist(fullPlaylistUrl: string, courtSystemId: Uuid): ApiResult<Buffer | undefined> {
    const playlistUrl = fullPlaylistUrl.split(`${API_PATH}/`)[1]
    return this.regionalApiClient(API_PATH, courtSystemId).pipe(
      switchMap(client =>
        client.get<Buffer | undefined>({
          path: playlistUrl,
          responseType: HttpResponseType.ArrayBuffer,
        }),
      ),
      recoverType(NotFoundApiError, undefined),
    )
  }

  hasBeenReleasedToUser(audioSegmentId: Uuid): ApiResult<HasBeenReleasedToUserResponse> {
    return this.apiClient.get({
      path: `/${audioSegmentId}/hasBeenReleasedToUser`,
    })
  }

  setShareStatus(
    audioSegmentId: Uuid,
    shareStatus: AudioSegmentShareStatus,
  ): ApiResult<AudioSegmentShareStatusResponse> {
    return this.apiClient.put({
      path: `/${audioSegmentId}/shareStatus`,
      body: { shareStatus },
    })
  }

  getShareStatus(audioSegmentId: Uuid): ApiResult<AudioSegmentShareStatusResponse> {
    return this.apiClient.get({ path: `/${audioSegmentId}/shareStatus` })
  }

  unarchiveAssociatedRecording(audioSegmentId: Uuid): ApiResult<void> {
    return this.apiClient.post({ path: `/${audioSegmentId}/unarchiveAssociatedRecording` })
  }

  getRecordingId(audioSegmentId: Uuid): ApiResult<Uuid> {
    return this.getWithRecordingSegments(audioSegmentId).pipe(mapData(data => data.recordingSegments[0].recordingId))
  }

  getSealedSegments(audioSegmentId: Uuid, courtSystemId: Uuid, dataStore: DataStore): ApiResult<SealedSegments> {
    const request = {
      path: `/${audioSegmentId}/sealed-segments`,
      responseBodyType: SealedSegments,
    }

    if (dataStore === DataStore.Core) {
      return this.apiClient.get(request)
    }

    return this.regionalApiClient(API_PATH, courtSystemId).pipe(
      switchMap(apiClient => apiClient.get<SealedSegments>(request)),
    )
  }

  /**
   * Retrieve underlying producer conditions for an audio segments recording, to display on the realtime ordering
   * playback page.
   */
  getProducerConditionsForAudioSegment(audioSegmentId: Uuid, courtSystemId: Uuid): ApiResult<ProducerCondition[]> {
    return this.regionalApiClient(API_PATH, courtSystemId).pipe(
      switchMap(client => {
        return client.get<ProducerCondition[] | undefined>({
          path: `/${audioSegmentId}/conditions`,
          headers: { courtSystemId },
        })
      }),
      recover([]),
      recoverUndefinedData([]),
      mapData(response => response?.map(x => plainToClass(ProducerCondition, x)) ?? []),
    )
  }

  pollLiveAudioSegment(
    audioSegment: AudioSegmentPlaybackData,
    pollFrequencySeconds = AUDIO_SEGMENT_POLL_FREQUENCY,
  ): ApiResult<AudioSegmentPlaybackData> {
    return timer(0, pollFrequencySeconds * ONE_SECOND_MS).pipe(
      switchMap(() => this.getWithRecordingSegmentsLive(audioSegment.id, audioSegment.courtSystemId)),
      skipFutureNonSuccess(),
      distinctUntilDataChanged((a: AudioSegmentPlaybackData, b: AudioSegmentPlaybackData) => {
        return a?.id === b?.id && a.isLive === b.isLive && hasEqualSegments(a.recordingSegments, b.recordingSegments)
      }),
    )
  }
}
